import { DialogActions } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import Grid from "@mui/material/Grid2"
import { ArcElement, Chart as ChartJS, Legend, Tooltip, type ChartType, type DefaultDataPoint } from "chart.js"
import { useContext, useEffect, useRef, useState } from "react"
import { Pie } from "react-chartjs-2"
import { APIContext } from "../../../Contexts/APIContext"
import MinMaxParam from "../../../TripRate/ViewModels/MinMaxViewModel"
import PieChartOptions from "../../../Utilities/PieChartOptions"
import ExportChartDialog from "../../ExportChartDialog"
import ExportChartViewModel from "../../ViewModels/ExportChartViewModel"
import PieChartOptionsViewModel from "../../../TripRate/ViewModels/PieChartOptionsViewModel"
import { getPieBackgroundColours, getPieBorderColours } from "../../../Utilities/PieChartPalette"

ChartJS.register(ArcElement, Tooltip, Legend)

interface PieSegment {
    label: string
    percentage: number
}

interface IProps {
    onClose: () => void
    open: boolean
    surveyId: string
    date: string
}

type ChartOrUndef<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown> =
    ChartJS<TType, TData, TLabel> | undefined

export default function VehiclePercentageChart({ open, onClose, surveyId, date }: IProps) {
    const { post, postPDF, get } = useContext(APIContext)
    const [data, setData] = useState<PieSegment[]>([])
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [minMaxTimes, setMinMaxTimes] = useState<MinMaxParam>(new MinMaxParam())
    const chartRef = useRef<ChartOrUndef<"pie", number[], string>>(undefined)
    const [pieOptions, setPieOptions] = useState<PieChartOptionsViewModel>(new PieChartOptionsViewModel())
    const pieOptionsRef = useRef<PieChartOptionsViewModel>(pieOptions)
    const [, surveyMonth, surveyYear] = date.split("/").map(Number)
    const usesLegacyPercentages =
        Number.isFinite(surveyMonth) &&
        Number.isFinite(surveyYear) &&
        (surveyYear < 2013 || (surveyYear === 2013 && surveyMonth < 3))

    useEffect(() => {
        pieOptionsRef.current = pieOptions
    }, [pieOptions])


    useEffect(() => {
        if (open) {
            if (!usesLegacyPercentages) {
                getSurveyTimes()
            }
            getData()
        }
    }, [open])

    const getData = (options?: PieChartOptionsViewModel) => {
        const vm = {
            surveyId,
            date,
            options: options
        }
        post("Chart", "GetVehiclePercentagesPieData", vm, setData)
    }

    const getSurveyTimes = () => {
        get(`SurveyDays`, `GetAvailableTimeRangeForSurvey?surveyReference=${surveyId}`, setMinMaxTimes)
    }

    const exportGraph = () => setExportDialogOpen(true)

    const getReport = (exportFilters: ExportChartViewModel) => {
        exportFilters.data = data
        exportFilters.surveyDate = date
        exportFilters.surveyRef = surveyId
        exportFilters.options = pieOptionsRef.current
        exportFilters.chartTitle = "Vehicle Percentages"
        exportFilters.type === "PDF" ? getChartPDF(exportFilters) : getChartJPEG()
    }

    const getChartPDF = (exportFilters: ExportChartViewModel) => {
        postPDF("PDFGenerator", "GetVehiclePercentagesPDF", exportFilters.fileName, false, exportFilters)
    }

    const getChartJPEG = () => {
        downloadJPEG()
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        customCanvasBackgroundColor: { color: "#ffffff" },
        backgroundColor: "#ffffff",
        plugins: {
            backgroundColor: "#ffffff",
            legend: { display: true },
            title: {
                display: true,
                text: `Vehicle Percentages For: ${surveyId} Surveyed: ${date}`,
                font: { size: 14, weight: "bold" as const },
                padding: 20,
            },
            tooltip: {
                displayColors: false,
                callbacks: {
                    title: () => "",
                    label: (context: any) => {
                        const item = data[context.dataIndex]
                        return [`${item.label}`, `Percentage: ${item.percentage.toFixed(1) || 0}%`]
                    },
                },
            },
        },
    }

    const chartData = {
        labels: data.map((m) => `${m.label} ${m.percentage.toFixed(1)}%`),
        datasets: [
            {
                data: data.map((d) => d.percentage),
                dataLabels: data.map((d) => d.percentage.toFixed(1) + "%"),
                backgroundColor: getPieBackgroundColours(data),
                borderColor: getPieBorderColours(data),
                borderWidth: 1,
            },
        ],
    }

    const downloadJPEG = () => {
        const chart = chartRef.current
        if (!chart) return

        const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)


        const src = chart.canvas as HTMLCanvasElement
        const padding = 48
        const out = document.createElement("canvas")
        out.width = src.width
        out.height = src.height + padding
        const octx = out.getContext("2d")
        if (!octx) return

        octx.fillStyle = "#ffffff"
        octx.fillRect(0, 0, out.width, out.height)
        octx.drawImage(src, 0, 0)

        let infoText = `Direction: ${capitalize(pieOptions.direction)}, Period: ${capitalize(pieOptions.period)}`
        if (pieOptions.selectedTime) {
            infoText += `, Time: ${pieOptions.selectedTime.min}:00 - ${pieOptions.selectedTime.max}`
        }

        octx.fillStyle = "#000000"
        octx.font = "16px Arial"
        octx.textAlign = "center"
        octx.fillText(infoText, out.width / 2, src.height + 28)

        const dataUrl = out.toDataURL("image/jpeg", 0.95)
        const a = document.createElement("a")
        a.href = dataUrl
        a.download = `vehicle-percentages-${surveyId}-${date}.jpg`
        a.click()
    }

    const handleOptionsChange = (options: PieChartOptionsViewModel) => {
        pieOptionsRef.current = options
        setPieOptions(options);
        getData(options)
    }

    return (
        <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
            <div style={{ padding: 20 }}>
                <div style={{ width: 500, height: 500, margin: "0 auto" }}>
                    <Pie ref={chartRef} data={chartData} options={options} />
                </div>
                <Grid container style={{ marginTop: 20 }}>
                    <Grid size={12}>
                        {!usesLegacyPercentages && (
                            <PieChartOptions minMaxTimes={minMaxTimes} getOptions={handleOptionsChange} />
                        )}
                    </Grid>
                </Grid>
            </div>
            <DialogActions>
                <Button fullWidth={false} onClick={() => onClose()}>Close</Button>
                <Button fullWidth={false} onClick={exportGraph}>Export</Button>
            </DialogActions>
            <ExportChartDialog
                dialogOpen={exportDialogOpen}
                handleClose={() => setExportDialogOpen(false)}
                exportData={getReport}
            />
        </Dialog>
    )
}
